import React, { useCallback, useEffect, useMemo, useState } from "react";
import config from "../config.js";
import { apiErrorMessage } from "../utils/apiError";

/**
 * Earmark sheets of a material for a sales order (2026-10-13, Mike).
 *
 * Until now an earmark could only be placed on a PO line, at the moment of buying
 * (POSalesOrderEarmarks). This dialog earmarks sheets already on the floor (a pallet:
 * one tracking number) or still on order, from the material page ("Earmark Sheets…")
 * or a tracking number's card ("Add earmark", locked to that pallet).
 *
 * An earmark is a real reservation, prints on the material label (FOR: SO-…), shows on
 * the pallet's beacon ("5 left, 2 earmarked"), and keeps other orders' jobs off the
 * pallet in Find Material. It releases itself when the order's own job uses the sheets
 * or the order closes; Release ends it by hand.
 *
 * API: GET /materials/{id}/earmark_sources; POST /material_tracking/{mtn}/earmarks
 * (a pallet: the quantity is the sheets to HOLD); POST /purchase_orders/lines/{line}/allocations
 * (an inbound line: as on the PO page); POST /purchase_orders/allocations/{id}/release.
 */
const CLOSED = ["Cancelled", "Completed", "Closed", "Voided"];

const money = (v) => (v === null || v === undefined || v === "" ? null
  : Number(v).toLocaleString(undefined, { style: "currency", currency: "USD" }));

function sourceKey(kind, id) {
  return `${kind}:${id}`;
}

export default function EarmarkSheetsModal({ show, onHide, materialId, materialCode, mtnId = null, onChanged }) {
  const API = config.API_BASE_URL;
  const [sources, setSources] = useState(null);
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [source, setSource] = useState("");
  const [soId, setSoId] = useState("");
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    if (!materialId) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/materials/${materialId}/earmark_sources`);
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      setSources(await res.json());
    } catch (e) {
      setError(e.message || "Could not load this material's pallets.");
    } finally {
      setLoading(false);
    }
  }, [API, materialId]);

  useEffect(() => {
    if (!show) {
      setSources(null);
      setError("");
      setNotice("");
      setSource("");
      setSoId("");
      setQty("");
      setNote("");
      return;
    }
    load();
    (async () => {
      try {
        const res = await fetch(`${API}/sales_orders?status=Open`);
        if (!res.ok) return;
        const rows = await res.json();
        setSalesOrders(Array.isArray(rows) ? rows.filter(so => !CLOSED.includes(so.status)) : []);
      } catch (e) { /* the picker stays empty; the dialog still shows the pallets */ }
    })();
  }, [show, load, API]);

  const lots = useMemo(() => {
    const all = (sources && Array.isArray(sources.lots)) ? sources.lots : [];
    return mtnId ? all.filter(l => l.mtn_id === mtnId) : all;
  }, [sources, mtnId]);
  const poLines = useMemo(
    () => (!mtnId && sources && Array.isArray(sources.po_lines)) ? sources.po_lines : [],
    [sources, mtnId]
  );

  // A dialog opened from one pallet's card picks that pallet.
  useEffect(() => {
    if (mtnId && lots.length === 1 && !source) setSource(sourceKey("mtn", mtnId));
  }, [mtnId, lots, source]);

  const chosen = useMemo(() => {
    if (!source) return null;
    const [kind, id] = source.split(":");
    if (kind === "mtn") {
      const lot = lots.find(l => String(l.mtn_id) === id);
      return lot ? { kind, lot, free: lot.available } : null;
    }
    const line = poLines.find(l => String(l.po_line_id) === id);
    return line ? { kind, line, free: line.available } : null;
  }, [source, lots, poLines]);

  const save = async () => {
    if (!chosen) { setError("Pick a pallet or an order line first."); return; }
    if (!soId) { setError("Pick a sales order first."); return; }
    const n = Number(qty) || 0;
    if (n <= 0) { setError("Enter how many sheets to earmark."); return; }
    setBusy(true);
    setError("");
    setNotice("");
    let who = null;
    try { who = sessionStorage.getItem("userName"); } catch (e) { /* no session storage */ }
    try {
      const url = chosen.kind === "mtn"
        ? `${API}/material_tracking/${chosen.lot.mtn_id}/earmarks`
        : `${API}/purchase_orders/lines/${chosen.line.po_line_id}/allocations`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sales_order_id: Number(soId), quantity: n, note: note.trim() || null,
                               created_by_name: who }),
      });
      // The API names what is already held when it refuses: show it as it is.
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const body = await res.json();
      setNotice(body.message || "Earmarked.");
      setQty("");
      setNote("");
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.message || "Could not save the earmark.");
    } finally {
      setBusy(false);
    }
  };

  const release = async (e) => {
    if (!window.confirm(`Release ${e.order_number}'s earmark? The sheets go back to stock.`)) return;
    setError("");
    setNotice("");
    let who = null;
    try { who = sessionStorage.getItem("userName"); } catch (x) { /* no session storage */ }
    try {
      const res = await fetch(`${API}/purchase_orders/allocations/${e.po_line_allocation_id}/release`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ release_reason: "Released from the material screen", released_by_name: who }),
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      setNotice(`${e.order_number}'s earmark released.`);
      await load();
      if (onChanged) onChanged();
    } catch (x) {
      setError(x.message || "Could not release the earmark.");
    }
  };

  if (!show) return null;

  const code = materialCode || sources?.material?.material_code || "";
  const renderEarmarks = (list, releasable) => (list || []).map(e => (
    <div key={`${e.source}-${e.po_line_allocation_id}`} className="d-flex flex-wrap align-items-center gap-2 small mt-1">
      <span className="badge bg-warning text-dark">
        <i className="bi bi-bookmark-fill me-1"></i>{e.order_number} × {e.open_quantity ?? e.quantity}
      </span>
      {e.customer_name && <span>{e.customer_name}</span>}
      {e.source === "po_line" && releasable === "pallet" && (
        <span className="text-muted">on PO {e.po_number}'s line</span>
      )}
      {(releasable === "any" || e.source === releasable) && (
        <button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={() => release(e)}>
          release
        </button>
      )}
    </div>
  ));

  return (
    <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
         data-testid="earmark-sheets-modal">
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header bg-light">
            <h5 className="modal-title">
              <i className="bi bi-bookmark-plus me-2"></i>
              Earmark sheets{code ? ` — ${code}` : ""}
            </h5>
            <button type="button" className="btn-close" onClick={onHide} disabled={busy}></button>
          </div>
          <div className="modal-body">
            <p className="small text-muted mb-3">
              Holds sheets for a sales order: they are reserved for it, the label prints
              <strong> FOR: SO-…</strong>, the pallet's beacon shows how many are earmarked, and Find
              Material keeps other orders' jobs off them. It releases itself when the order's own job
              uses the sheets or the order closes.
            </p>
            {error && <div className="alert alert-warning py-2" role="alert">{error}</div>}
            {notice && <div className="alert alert-success py-2" role="status">{notice}</div>}
            {loading && !sources && <div className="text-muted small mb-2">Loading pallets…</div>}

            {sources && (
              <>
                <h6 className="mb-1">On the floor</h6>
                {lots.length === 0 ? (
                  <div className="small text-muted mb-3">
                    {mtnId ? "This tracking number has no sheets left to hold." : "No pallets of this material with sheets left."}
                  </div>
                ) : (
                  <table className="table table-sm align-middle mb-3">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 32 }}></th>
                        <th>TRK#</th>
                        <th>PO / Heat</th>
                        <th className="text-end">Unit cost</th>
                        <th className="text-end">Left</th>
                        <th className="text-end">Free</th>
                        <th>Earmarked for</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lots.map(l => {
                        const key = sourceKey("mtn", l.mtn_id);
                        return (
                          <tr key={key} className={source === key ? "table-warning" : ""}
                              onClick={() => setSource(key)} style={{ cursor: "pointer" }}>
                            <td>
                              <input type="radio" className="form-check-input" name="earmark-source"
                                     aria-label={`TRK ${l.trk}`} checked={source === key}
                                     onChange={() => setSource(key)} />
                            </td>
                            <td>
                              <strong>{l.trk}</strong>
                              {l.beacon_id && <div className="small text-muted"><i className="bi bi-broadcast me-1"></i>{l.beacon_id}</div>}
                            </td>
                            <td className="small">
                              {l.po_number ? `PO ${l.po_number}` : (l.source_type === "customer_intake" ? "Customer" : "—")}
                              {l.heat_number && <div className="text-muted">Heat {l.heat_number}</div>}
                            </td>
                            <td className="text-end small">{money(l.cost_per_unit) || "—"}</td>
                            <td className="text-end">{l.remaining}</td>
                            <td className="text-end">{l.available}</td>
                            <td>{renderEarmarks(l.earmarks, "pallet")}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {!mtnId && (
                  <>
                    <h6 className="mb-1">On order (not received yet)</h6>
                    {poLines.length === 0 ? (
                      <div className="small text-muted mb-3">No open purchase orders for this material.</div>
                    ) : (
                      <table className="table table-sm align-middle mb-3">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 32 }}></th>
                            <th>PO #</th>
                            <th>Supplier</th>
                            <th>ETA</th>
                            <th className="text-end">Ordered</th>
                            <th className="text-end">Free</th>
                            <th>Earmarked for</th>
                          </tr>
                        </thead>
                        <tbody>
                          {poLines.map(ln => {
                            const key = sourceKey("line", ln.po_line_id);
                            return (
                              <tr key={key} className={source === key ? "table-warning" : ""}
                                  onClick={() => setSource(key)} style={{ cursor: "pointer" }}>
                                <td>
                                  <input type="radio" className="form-check-input" name="earmark-source"
                                         aria-label={`PO ${ln.po_number}`} checked={source === key}
                                         onChange={() => setSource(key)} />
                                </td>
                                <td><strong>{ln.po_number}</strong></td>
                                <td className="small">{ln.supplier || "—"}</td>
                                <td className="small">
                                  {ln.estimated_receive_date ? new Date(`${ln.estimated_receive_date}T12:00:00`).toLocaleDateString() : "—"}
                                </td>
                                <td className="text-end">{ln.quantity_ordered}</td>
                                <td className="text-end">{ln.available}</td>
                                <td>{renderEarmarks(ln.earmarks, "any")}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </>
                )}

                <div className="border rounded p-2 bg-light">
                  <div className="row g-2 align-items-end">
                    <div className="col-md-5">
                      <label className="form-label small mb-0" htmlFor="earmark-so">Sales order</label>
                      <select id="earmark-so" className="form-select form-select-sm" value={soId}
                              onChange={(e) => setSoId(e.target.value)}>
                        <option value="">— select a sales order —</option>
                        {salesOrders.map(so => (
                          <option key={so.sales_order_id} value={so.sales_order_id}>
                            {so.order_number}{so.customer_name ? ` — ${so.customer_name}` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small mb-0" htmlFor="earmark-qty">
                        {chosen?.kind === "line" ? "Sheets" : "Sheets to hold"}
                      </label>
                      <input id="earmark-qty" type="number" min="1" className="form-control form-control-sm"
                             placeholder={chosen && chosen.free > 0 ? String(chosen.free) : ""}
                             value={qty} onChange={(e) => setQty(e.target.value)} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small mb-0" htmlFor="earmark-note">Note (optional)</label>
                      <input id="earmark-note" type="text" className="form-control form-control-sm" maxLength={200}
                             value={note} onChange={(e) => setNote(e.target.value)} />
                    </div>
                    <div className="col-md-2 d-grid">
                      <button type="button" className="btn btn-sm btn-warning"
                              disabled={busy || !chosen || !soId || !(Number(qty) > 0)} onClick={save}>
                        {busy ? "Saving…" : "Earmark"}
                      </button>
                    </div>
                  </div>
                  <div className="small text-muted mt-1">
                    {!chosen ? "Pick a pallet above (or an order line) first."
                      : chosen.kind === "mtn"
                        ? `TRK ${chosen.lot.trk}: ${chosen.lot.available} of ${chosen.lot.remaining} free. Earmarking the same order again changes how many it holds.`
                        : `PO ${chosen.line.po_number}: ${chosen.line.available} of ${chosen.line.quantity_ordered} free. Earmarking the same order again changes its quantity.`}
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onHide} disabled={busy}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}
